import { getSessionId } from '@aspendental/shared-utils/utils';
import { format, isValid } from 'date-fns';

import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import type { FormSubmission } from '@src/lib/__generated/user-events-api';
import useAppStore from '@src/store/modules/app.store';
import { StateSchema } from '@src/store/modules/patient-information.form.store';
import restfulClient from '@src/utils/restfulClient';

interface SchedulerLead {
	shouldUseMottoConfig: boolean;
	firstName: string;
	lastName: string;
	email: string;
	dateOfBirth: Date | null;
	reasonForVisit: DigitalVisitReasonEnum | '';
}

export const schedulerLeadEvent = async (lead: SchedulerLead): Promise<void> => {
	try {
		const firstName = StateSchema.firstName.safeParse(lead.firstName);
		const lastName = StateSchema.lastName.safeParse(lead.lastName);
		const email = StateSchema.email.safeParse(lead.email);
		if (
			!firstName.success ||
			!lastName.success ||
			!email.success ||
			!(lead.dateOfBirth instanceof Date) ||
			!isValid(lead.dateOfBirth) ||
			!lead.reasonForVisit
		) {
			return;
		}

		const answers = {
			first_name: firstName.data,
			last_name: lastName.data,
			email_address: email.data,
			// DOB is a calendar date, not a UTC timestamp.
			date_of_birth: format(lead.dateOfBirth, 'yyyy-MM-dd'),
			reason_for_visit: lead.reasonForVisit,
		};
		const { shouldUseMottoConfig } = lead;
		const { formEventsUrl } = useAppStore.getState();
		const payload: FormSubmission = {
			form: {
				brand: shouldUseMottoConfig ? 'Motto' : 'Aspen Dental',
				product_line: shouldUseMottoConfig ? 'Motto' : 'Aspen Dental',
				form_title: shouldUseMottoConfig ? 'motto-scheduler' : 'aspen-scheduler',
				form_description: shouldUseMottoConfig ? 'Motto Scheduler' : 'Aspen Scheduler',
				form_type: 'Quiz',
				form_channel: 'Web',
				session_id: getSessionId() ?? '',
				form_questions: Object.entries(answers).map(([question, answer]) => ({
					question,
					answer,
					unique_label: question,
				})),
			},
		};

		await restfulClient.post({
			domain: formEventsUrl,
			route: '',
			data: payload,
		});
	} catch {
		// Keep lead capture failures non-blocking and patient data out of diagnostics.
		console.error('[schedulerLeadEvent] Unable to submit scheduler lead');
	}
};
